import type { ReactElement } from 'react';
import React from 'react';
import { t } from '@lingui/macro';

import Check from '@rover/icons/svg/check.svg';
import Plus from '@rover/icons/svg/plus.svg';
import { type ButtonProps, Box, Button } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import useShortlist from '../hooks/useShortlist';

export type Props = ButtonProps & {
  personOpk: string;
  removedText?: string;
  removedIcon?: React.ComponentType<unknown> | null;
  removedVariant?: ButtonProps['variant'];
  addedText?: string;
  addedIcon?: React.ComponentType<unknown> | null;
  addedVariant?: ButtonProps['variant'];
};

export default function AddToShortlistButton({
  personOpk,
  removedText = t`Add to contact list`,
  removedVariant = 'default',
  removedIcon = Plus,
  addedText = t`Added to contact list`,
  addedVariant = 'default',
  addedIcon = Check,
  ...other
}: Props): ReactElement {
  const { add, remove, isAdded, isAtMax } = useShortlist();
  const added = isAdded(personOpk);
  const theme = useTheme();

  const actualAddedIcon = addedIcon === null ? undefined : Check;
  const actualRemovedIcon = removedIcon === null ? undefined : Plus;

  const disabled = isAtMax && !added;

  return (
    <Button
      variant={added ? addedVariant : removedVariant}
      icon={added ? actualAddedIcon : actualRemovedIcon}
      isSelected={added}
      aria-pressed={added}
      disabled={disabled}
      sx={{
        backgroundColor: added ? theme.background.accent_secondary : undefined,
        borderColor: added ? theme.colors.border.input_focus : undefined,
      }}
      onPress={({ webEvent }) => {
        webEvent?.stopPropagation();
        if (added) {
          remove(personOpk);
        } else {
          add(personOpk);
        }
      }}
      {...other}
    >
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {added ? addedText : removedText}
      </Box>
    </Button>
  );
}
